import { useCallback, useMemo } from 'react';
import type { Field } from '@formily/core';
import { v4 as uuidv4 } from 'uuid';
import { isPlainObject, mapValues } from 'lodash';
import { IRowActions } from '../../components/Table/utils/types';
import { parseTemplateProps } from '../utils/data-parser';

type FormArrayTableRecord = Record<string, unknown>;

const isUploadedFile = (value: unknown): value is Record<string, unknown> => {
  if (!isPlainObject(value)) return false;
  const obj = value as Record<string, unknown>;
  return 'id' in obj && 'disk' in obj && 'bucket' in obj && 'path' in obj;
};

const isFileUploadArray = (
  value: unknown
): value is Array<Record<string, unknown>> => {
  return (
    Array.isArray(value) && value.length > 0 && value.every(isUploadedFile)
  );
};

const createRowsFromFiles = (
  values: Record<string, unknown>,
  fileFieldName: string,
  files: Array<Record<string, unknown>>
): FormArrayTableRecord[] => {
  return files.map(file => ({
    ...mapValues(values, (val, key) => (key === fileFieldName ? file : val)),
    id: uuidv4(),
  }));
};

interface IUseFormArrayTableActionsParams {
  dataSource: FormArrayTableRecord[];
  field: Field;
  canEditRow: boolean;
  canDeleteRow: boolean;
  canDuplicateRow: boolean;
  canEditRowConfig: any; // TODO: Add type
  canDeleteRowConfig: any; // TODO: Add type
  canDuplicateRowConfig: any; // TODO: Add type
  setEditingIndex: (index: number | null) => void;
  setDeletingIndex: (index: number | null) => void;
  setHighlightedRowId: (id: string | null) => void;
  show: () => void;
  showDeleteConfirm: () => void;
  hide: () => void;
  hideDeleteConfirm: () => void;
  editingIndex: number | null;
  pageData: any;
  disabled?: boolean;
  rowItemName?: string;
}

export const useFormArrayTableActions = ({
  dataSource,
  field,
  canEditRow,
  canDeleteRow,
  canDuplicateRow,
  canEditRowConfig,
  canDeleteRowConfig,
  canDuplicateRowConfig,
  setEditingIndex,
  setDeletingIndex,
  setHighlightedRowId,
  show,
  showDeleteConfirm,
  hide,
  hideDeleteConfirm,
  editingIndex,
  pageData,
  disabled,
  rowItemName = 'row',
}: IUseFormArrayTableActionsParams) => {
  const findRecordIndex = useCallback(
    (record: FormArrayTableRecord) =>
      dataSource.findIndex(item => item === record),
    [dataSource]
  );

  const handleEdit = useCallback(
    (record: FormArrayTableRecord) => {
      const index = findRecordIndex(record);
      if (index !== -1) {
        setEditingIndex(index);
        show();
      }
    },
    [findRecordIndex, setEditingIndex, show]
  );

  const handleView = useCallback(
    (record: FormArrayTableRecord) => {
      const index = findRecordIndex(record);
      if (index !== -1) {
        setEditingIndex(index);
        show();
      }
    },
    [findRecordIndex, setEditingIndex, show]
  );

  const handleDelete = useCallback(
    (record: FormArrayTableRecord) => {
      const index = findRecordIndex(record);
      if (index !== -1) {
        setDeletingIndex(index);
        showDeleteConfirm();
      }
    },
    [findRecordIndex, setDeletingIndex, showDeleteConfirm]
  );

  const handleAdd = useCallback(() => {
    setEditingIndex(null);
    show();
  }, [setEditingIndex, show]);

  const handleDuplicate = useCallback(
    (record: FormArrayTableRecord) => {
      // Insert into the FULL field value (tombstoned rows included) right after the
      // source row, matched by reference. Only the new row's own id must be fresh —
      // two elements sharing an id would both diff onto the same target row, so the
      // clone would be merged into the original instead of created. Nested row ids
      // are reassigned by the BE on create, so they can be carried over as they are.
      const current = Array.isArray(field.value) ? field.value : [];
      const index = current.findIndex(item => item === record);
      if (index === -1) return;

      const clone = { ...record, id: uuidv4(), _formLocal: true };
      field.setValue([
        ...current.slice(0, index + 1),
        clone,
        ...current.slice(index + 1),
      ]);
      setHighlightedRowId(clone.id);
    },
    [field, setHighlightedRowId]
  );

  const handleCancel = useCallback(() => {
    setEditingIndex(null);
    hide();
  }, [setEditingIndex, hide]);

  const handleSubmit = useCallback(
    (values: Record<string, unknown>) => {
      if (editingIndex !== null) {
        // Apply the edit against the FULL field value so tombstoned rows are
        // preserved (rebuilding from `dataSource` would drop them). Match by
        // reference — the same object lives in both `dataSource` and field.value.
        const target = dataSource[editingIndex];
        const current = Array.isArray(field.value) ? field.value : [];
        field.setValue(
          current.map(item => (item === target ? { ...item, ...values } : item))
        );
        setEditingIndex(null);
        hide();
        return;
      }

      const fileUploadField = Object.entries(values).find(([_, value]) =>
        isFileUploadArray(value)
      );

      const newRows = fileUploadField
        ? createRowsFromFiles(
            values,
            fileUploadField[0],
            fileUploadField[1] as Array<Record<string, unknown>>
          )
        : [{ ...values, id: uuidv4(), _formLocal: true }];

      field.setValue([...(field.value || []), ...newRows]);
      setEditingIndex(null);
      hide();
    },
    [dataSource, field, setEditingIndex, hide, editingIndex]
  );

  const handleConfirmDelete = useCallback(
    (deletingIndex: number | null) => () => {
      if (deletingIndex === null) return;
      const target = dataSource[deletingIndex];
      if (!target) return;

      // Operate on the FULL field value (already-tombstoned rows included), not
      // `dataSource` (which filters them out). Rebuilding from `dataSource` would
      // drop earlier `toBeDeleted` rows, so only the last-deleted row would keep
      // its marker. Match the target by reference (same object as in field.value).
      const current = Array.isArray(field.value) ? field.value : [];
      if (target._formLocal) {
        // Unsaved row — never persisted, so just drop it (nothing to delete on BE).
        field.setValue(current.filter(item => item !== target));
      } else {
        field.setValue(
          current.map(item =>
            item === target ? { ...item, toBeDeleted: true } : item
          )
        );
      }
      setDeletingIndex(null);
      hideDeleteConfirm();
    },
    [dataSource, field, setDeletingIndex, hideDeleteConfirm]
  );

  const handleCancelDelete = useCallback(() => {
    setDeletingIndex(null);
    hideDeleteConfirm();
  }, [setDeletingIndex, hideDeleteConfirm]);

  const rowActions = useMemo(() => {
    const actions = [];
    if (disabled) {
      actions.push({ label: 'View', onClick: handleView });
    } else {
      if (canEditRow) {
        actions.push({ label: 'Edit', onClick: handleEdit });
      } else {
        actions.push({ label: 'View', onClick: handleView });
      }
      if (canDeleteRow)
        actions.push({ label: 'Delete', onClick: handleDelete });
    }
    return actions.length > 0 ? { menuItems: actions } : undefined;
  }, [
    canEditRow,
    canDeleteRow,
    disabled,
    handleEdit,
    handleDelete,
    handleView,
  ]);

  const rowActionsCallback = useCallback(
    (
      record: FormArrayTableRecord
    ): IRowActions<FormArrayTableRecord> | undefined => {
      const actions = [];
      const localCanEditRow =
        typeof canEditRow === 'boolean'
          ? canEditRow
          : parseTemplateProps({
              componentPropKey: 'canEditRow',
              componentPropValue: canEditRow,
              data: { ...pageData.context, [rowItemName]: record },
              config: canEditRowConfig,
              options: {},
            });

      const localCanDeleteRow =
        typeof canDeleteRow === 'boolean'
          ? canDeleteRow
          : parseTemplateProps({
              componentPropKey: 'canDeleteRow',
              componentPropValue: canDeleteRow,
              data: { ...pageData.context, [rowItemName]: record },
              config: canDeleteRowConfig,
              options: {},
            });

      // Opt-in flag (defaults to false), so it shows only when explicitly truthy —
      // unlike canEditRow/canDeleteRow, which are allowed unless set to false.
      const localCanDuplicateRow =
        typeof canDuplicateRow === 'boolean'
          ? canDuplicateRow
          : parseTemplateProps({
              componentPropKey: 'canDuplicateRow',
              componentPropValue: canDuplicateRow,
              data: { ...pageData.context, [rowItemName]: record },
              config: canDuplicateRowConfig,
              options: {},
            });

      if (disabled) {
        actions.push({ label: 'View', onClick: handleView });
      } else {
        if (localCanEditRow !== false) {
          actions.push({ label: 'Edit', onClick: handleEdit });
        } else {
          actions.push({ label: 'View', onClick: handleView });
        }
        if (localCanDuplicateRow)
          actions.push({ label: 'Duplicate', onClick: handleDuplicate });
        if (localCanDeleteRow !== false)
          actions.push({ label: 'Delete', onClick: handleDelete });
      }
      return actions.length > 0 ? { menuItems: actions } : undefined;
    },
    [
      canEditRow,
      canDeleteRow,
      canDuplicateRow,
      disabled,
      handleEdit,
      handleDelete,
      handleDuplicate,
      handleView,
      pageData,
      rowItemName,
    ]
  );

  return {
    rowActions,
    handleAdd,
    handleCancel,
    handleSubmit,
    handleConfirmDelete,
    handleCancelDelete,
    rowActionsCallback,
  };
};
